import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { CadsAppButtonComponent } from '../../components/cads-app-button/cads-app-button.component';
import type { CadsAppButtonConfig } from '../../components/cads-app-button/cads-app-button.config';

import { CadsCheckboxGroupComponent } from '../cads-checkbox-group/cads-checkbox-group.component';
import type { CadsCheckboxGroupConfig } from '../cads-checkbox-group/cads-checkbox-group.config';
import { CadsInputComponent } from '../cads-input/cads-input.component';
import type { CadsInputConfig } from '../cads-input/cads-input.config';
import { CadsRadioGroupComponent } from '../cads-radio-group/cads-radio-group.component';
import type { CadsRadioGroupConfig } from '../cads-radio-group/cads-radio-group.config';
import { CadsSelectComponent } from '../cads-select/cads-select.component';
import type { CadsSelectConfig } from '../cads-select/cads-select.config';
import { CadsTextareaComponent } from '../cads-textarea/cads-textarea.component';
import type { CadsTextareaConfig } from '../cads-textarea/cads-textarea.config';

import type { CadsFormControlConfig } from '../controls/cads-form-control-config';
import { pickCadsFormErrorMessage } from '../controls/cads-form-error-message';

import type { CadsFormConfig } from './cads-form.config';

/**
 * Config-driven reactive form renderer. Builds a `FormGroup` from a {@link CadsFormConfig} (one entry per control),
 * dispatches each entry to the right form-kit component (`cads-input`, `cads-textarea`, `cads-checkbox-group`, etc.),
 * and exposes a `(submitted)` output that fires the raw value on valid submit (after `markAllAsTouched`). On invalid
 * submit, the form just paints validation chrome.
 *
 * The component does **not** own copy or i18n: descriptors carry pre-translated `errorMessages`, surfaced through
 * {@link pickCadsFormErrorMessage}.
 */
@Component({
    selector: 'cads-form',
    imports: [
        ReactiveFormsModule,
        CadsAppButtonComponent,
        CadsCheckboxGroupComponent,
        CadsInputComponent,
        CadsRadioGroupComponent,
        CadsSelectComponent,
        CadsTextareaComponent,
    ],
    templateUrl: './cads-form.component.html',
    styleUrl: './cads-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-form-host',
    }
})
export class CadsFormComponent {
  readonly config = input.required<CadsFormConfig>();
  /** Emits the raw `FormGroup` value when the user submits a **valid** form. */
  readonly submitted = output<Record<string, unknown>>();

  protected readonly _attemptedSubmit = signal(false);

  /**
   * Rebuilds the `FormGroup` whenever `config().controls` changes (using the descriptors' own `FormControl`
   * instances). Resetting `_attemptedSubmit` here keeps validation chrome behavior consistent across config swaps.
   */
  protected readonly _form = computed(() => {
    const controls: Record<string, FormControl> = {};
    for (const c of this.config().controls) {
      controls[c.key] = c.control as FormControl;
    }
    return new FormGroup(controls);
  });

  /**
   * Tick signal bumped on every status / value change of the active form. Lets the per-control error computeds
   * re-run without us subscribing them individually.
   */
  protected readonly _validationTick = signal(0);

  constructor() {
    effect((onCleanup) => {
      const form = this._form();
      const status = form.statusChanges.subscribe(() => {
        untracked(() => this._validationTick.update((n) => n + 1));
      });
      const value = form.valueChanges.subscribe(() => {
        untracked(() => this._validationTick.update((n) => n + 1));
      });
      untracked(() => {
        this._attemptedSubmit.set(false);
        this._validationTick.update((n) => n + 1);
      });
      onCleanup(() => {
        status.unsubscribe();
        value.unsubscribe();
      });
    }, { allowSignalWrites: true });
  }

  protected readonly _submitButtonConfig = computed((): CadsAppButtonConfig => {
    const cfg = this.config();
    const disabled = cfg.disableSubmitWhenInvalid === true && this._form().invalid;
    void this._validationTick();
    return {
      label: cfg.submitLabel ?? 'Continue',
      disabled,
      hug: cfg.hugSubmit ?? false,
    };
  });

  protected _onSubmit(): void {
    this._attemptedSubmit.set(true);
    const form = this._form();
    form.markAllAsTouched();
    if (form.valid) {
      this.submitted.emit(form.getRawValue());
    }
  }

  protected _errorFor(c: CadsFormControlConfig): string | undefined {
    void this._validationTick();
    return pickCadsFormErrorMessage(c.control, c.errorMessages, this._attemptedSubmit());
  }

  /**
   * When `typeOptions.required` is **true** / **false**, that wins. Otherwise shows the required marker when the
   * descriptor control uses {@link Validators.required}.
   */
  protected _requiredIndicator(c: CadsFormControlConfig): boolean {
    const opts = c.typeOptions as { required?: boolean };
    if (opts.required === true) {
      return true;
    }
    if (opts.required === false) {
      return false;
    }
    return c.control.hasValidator(Validators.required);
  }

  protected _inputConfig(c: CadsFormControlConfig<'input'>): CadsInputConfig {
    return { ...c.typeOptions, errorMessage: this._errorFor(c), required: this._requiredIndicator(c) };
  }

  protected _textareaConfig(c: CadsFormControlConfig<'textarea'>): CadsTextareaConfig {
    return { ...c.typeOptions, errorMessage: this._errorFor(c), required: this._requiredIndicator(c) };
  }

  protected _checkboxGroupConfig(c: CadsFormControlConfig<'checkboxGroup'>): CadsCheckboxGroupConfig {
    return { ...c.typeOptions, errorMessage: this._errorFor(c), required: this._requiredIndicator(c) };
  }

  protected _radioGroupConfig(c: CadsFormControlConfig<'radioGroup'>): CadsRadioGroupConfig {
    return { ...c.typeOptions, errorMessage: this._errorFor(c), required: this._requiredIndicator(c) };
  }

  protected _selectConfig(c: CadsFormControlConfig<'select'>): CadsSelectConfig {
    return { ...c.typeOptions, errorMessage: this._errorFor(c), required: this._requiredIndicator(c) };
  }

  /* Narrow casts used in the template to keep type inference simple inside @switch branches. */
  protected _asInput(c: CadsFormControlConfig): CadsFormControlConfig<'input'> {
    return c as CadsFormControlConfig<'input'>;
  }
  protected _asTextarea(c: CadsFormControlConfig): CadsFormControlConfig<'textarea'> {
    return c as CadsFormControlConfig<'textarea'>;
  }
  protected _asCheckboxGroup(c: CadsFormControlConfig): CadsFormControlConfig<'checkboxGroup'> {
    return c as CadsFormControlConfig<'checkboxGroup'>;
  }
  protected _asRadioGroup(c: CadsFormControlConfig): CadsFormControlConfig<'radioGroup'> {
    return c as CadsFormControlConfig<'radioGroup'>;
  }
  protected _asSelect(c: CadsFormControlConfig): CadsFormControlConfig<'select'> {
    return c as CadsFormControlConfig<'select'>;
  }
}
